/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-08 10:50:39                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-02 11:16:19                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Styles
  * LeftNavigationBar: left navigation bar
*/

/* Imports */
import {
  Card,
  List,
  ListItem,
  Tooltip,
} from "@material-tailwind/react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  HomeIcon,
  DocumentPlusIcon,
  TruckIcon,
  BriefcaseIcon,
  CalculatorIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  ScissorsIcon,
  ShoppingCartIcon,
  WrenchScrewdriverIcon,
  ChartBarIcon
} from "@heroicons/react/24/outline";
import { useState, useEffect } from "react";
import { useUserRoles } from "../../../hooks/useUserRoles";

const navTooltipAnimation = {
  mount: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.22, ease: [0.16, 1, 0.3, 1] }
  },
  unmount: {
    opacity: 0,
    x: -12,
    scale: 0.96,
    transition: { duration: 0.12, ease: [0.4, 0, 1, 1] }
  }
};
/***/

/* Styles */
import "./LeftNavigationBar.scss";
import logo from "../../../logo.svg";
/***/

/*
* LeftNavigationBar: left navigation bar
* Contain icons that redirect to all principals or lists pages
* it turn into a burger menu on mobile
*/
export function LeftNavigationBar(props: { className?: string, isOpen?: boolean, onClose?: () => void }) {
  const { t } = useTranslation();
  const { isManager } = useUserRoles();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  // Fermer le menu mobile lors du clic sur un lien
  const handleLinkClick = () => {
    if (isMobile && props.onClose) {
      props.onClose();
    }
  };

  const navMenuItems = [
    {
      path: "/dashboard",
      icon: <HomeIcon />,
      tooltip: t("common:dashboard")
    }, {
      path: "create-workorder",
      icon: <DocumentPlusIcon />,
      tooltip: t("common:createIntervention")
    }, {
      path: "planning",
      icon: <CalendarDaysIcon />,
      tooltip: t("common:planning")
    }, {
      path: "/workorders",
      icon: <TruckIcon />,
      tooltip: t("common:workorders")
    }, {
      path: "/quotations",
      icon: <CalculatorIcon />,
      tooltip: t("common:quotations")
    }, {
      path: "/replenishments",
      icon: <ShoppingCartIcon />,
      tooltip: t("replenishment:title")
    },
    {
      path: "/parts",
      icon: <ScissorsIcon />,
      tooltip: t("common:parts")
    },
    {
      path: "/services",
      icon: <WrenchScrewdriverIcon />,
      tooltip: t("common:services:label")
    },
    {
      path: "/visits",
      icon: <BriefcaseIcon />,
      tooltip: t("common:visits")
    }, {
      path: "/customers",
      icon: <BuildingOfficeIcon />,
      tooltip: t("common:customers")
    }, {
      path: "/users",
      icon: <UserGroupIcon />,
      tooltip: t("common:users")
    },
    // Managers and above only — mirrors the RoleGuard on the route and the @Planner guard on
    // /stats/**. Hiding the entry is a courtesy; the API refuses the calls either way.
    ...(isManager ? [{
      path: "/statistics",
      icon: <ChartBarIcon />,
      tooltip: t("menu:statistics")
    }] : [])
  ];

  return (
    <>
      {/* Overlay pour mobile */}
      {isMobile && props.isOpen && (
        <div
          className="mobile-nav-overlay"
          onClick={props.onClose}
        />
      )}

      <Card className={`container ${props.className} ${isMobile && props.isOpen ? "mobile-open" : ""}`}>
        <div className="logo-container">
          <img src={logo} className="logo App-logo" alt="logo" />
        </div>

        <List className="list">
          {navMenuItems
            .map(({ path, icon, tooltip }) => {
              return (
                <NavLink to={path} key={path} onClick={handleLinkClick}>
                  <Tooltip className="h-[48px] pt-[14px] bg-gray-900 text-white rounded-none rounded-r origin-left pointer-events-none"
                    offset={0}
                    content={tooltip}
                    placement="right"
                    animate={navTooltipAnimation}>
                    <ListItem className="list-item"> {icon} </ListItem>
                  </Tooltip>
                </NavLink>
              );
            }
            )
          }

        </List>
      </Card>
    </>
  );
}
